import type { AlarmStatus, AlarmType } from '@empower-safety/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export interface AlarmDto {
  id: string;
  organisation_id: string;
  user_id: string;
  type: AlarmType;
  status: AlarmStatus;
  triggered_at: string;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_note: string | null;
  // Set when a monitor (or worker via PIN) closes the alarm. Drives the
  // alarm report's by-outcome breakdown. NULL on pre-migration rows and on
  // freshly-resolved rows where the monitor didn't pick one.
  resolution_outcome: AlarmResolutionOutcome | null;
  device_battery: number | null;
  // Where the worker was when they hit panic (detail response only). Null on
  // list rows and when the device had no fix at trigger time.
  trigger_lat: number | null;
  trigger_lng: number | null;
  trigger_accuracy_m: number | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface AlarmEventDto {
  id: string;
  alarm_id: string;
  event_type: string;
  occurred_at: string;
  actor_id: string | null;
  payload: Record<string, unknown>;
}

interface ListAlarmsQuery {
  status?: AlarmStatus;
  since?: string;
  limit?: number;
}

export function useAlarmsList(params: ListAlarmsQuery = {}) {
  const api = useApi();
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.since) qs.set('since', params.since);
  if (params.limit) qs.set('limit', String(params.limit));
  const search = qs.toString();

  return useQuery({
    queryKey: ['alarms', params],
    queryFn: () => api<{ alarms: AlarmDto[] }>(`/alarms${search ? `?${search}` : ''}`),
    // WebSocket is the primary push channel — see useLiveEvents.ts. It
    // invalidates this query on alarm.created / alarm.updated, so most state
    // changes land in the UI within tens of milliseconds. The 60s poll is a
    // defensive backup for the case where the socket has dropped and not yet
    // reconnected (the server's 5-minute event buffer + since_seq replay
    // already covers most disconnects, but a stale tab in the background
    // benefits from a slow poll to recover faster than the next visibility
    // change).
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useAlarm(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['alarms', id],
    queryFn: () => api<{ alarm: AlarmDto; events: AlarmEventDto[] }>(`/alarms/${id}`),
    enabled: Boolean(id),
  });
}

export function useAcknowledgeAlarm() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<AlarmDto>(`/alarms/${id}/acknowledge`, { method: 'POST' }),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['alarms'] });
      qc.invalidateQueries({ queryKey: ['alarms', id] });
    },
  });
}

export type AlarmResolutionOutcome = 'real_incident' | 'welfare_check_ok' | 'false_alarm';

export interface ResolveInput {
  id: string;
  note?: string;
  asFalseAlarm?: boolean;
  // Outcome classification picked on the resolve form. Counts toward the
  // alarm report's by-outcome breakdown.
  outcome?: AlarmResolutionOutcome;
}

export function useResolveAlarm() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note, asFalseAlarm, outcome }: ResolveInput) =>
      api<AlarmDto>(`/alarms/${id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ note, as_false_alarm: asFalseAlarm, outcome }),
      }),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['alarms'] });
      qc.invalidateQueries({ queryKey: ['alarms', id] });
    },
  });
}
